import { useSuperChatLogStore } from "@/store/useSuperChatLogStore";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

/**
 * SuperChatLogList 커스텀 훅
 *
 * @description 채팅 로그 리스트의 비즈니스 로직을 관리하는 훅
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export const useSuperChatLogList = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    sessions,
    loading,
    error,
    pagination,
    fetchSessions,
    exportToExcel,
    clearError,
    setPagination,
  } = useSuperChatLogStore();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // URL 파라미터에서 초기값 설정
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || ""
  );

  /**
   * 채팅 로그 데이터 로드
   */
  const loadChatLogData = useCallback(async () => {
    // URL 파라미터에서 페이지 정보 가져오기
    const currentPage = parseInt(searchParams.get("page") || "1", 10);
    const currentLimit = parseInt(searchParams.get("limit") || "10", 10);

    // 페이지네이션 상태 업데이트
    setPagination({
      page: currentPage,
      pageSize: currentLimit,
      limit: currentLimit,
    });

    const params = {
      page: currentPage,
      limit: currentLimit,
      search: searchTerm || undefined,
    };

    // Dashboard 전용 API 호출 (날짜 필터 없음)
    await fetchSessions(params);
  }, [searchTerm, searchParams, fetchSessions, setPagination]);

  // URL 파라미터 변경 시 데이터 로드
  useEffect(() => {
    loadChatLogData();
  }, [loadChatLogData]);

  // 에러 처리
  useEffect(() => {
    if (error) {
      setSnackbarMessage(error);
      setSnackbarOpen(true);
      clearError();
    }
  }, [error, clearError]);

  /**
   * Excel 내보내기 처리
   */
  const handleExport = async () => {
    try {
      const blob = await exportToExcel();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `chat-log-${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);

      setSnackbarMessage("데이터가 Excel 파일로 내보내기되었습니다.");
      setSnackbarOpen(true);
    } catch {
      // 에러는 스토어에서 처리됨
    }
  };

  /**
   * 검색어 변경 처리
   */
  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  };

  /**
   * 스낵바 닫기 처리
   */
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  /**
   * 세션 상세 보기 처리
   */
  const handleViewSession = (sessionId: string) => {
    navigate(`/chat/log/sessions/${sessionId}`);
  };

  /**
   * 페이지 변경 처리
   */
  const handlePageChange = useCallback(
    (newPage: number) => {
      // URL 파라미터 업데이트는 템플릿의 useList에서 처리
      // 여기서는 직접 API 호출만 수행
      const params = {
        page: newPage,
        limit: pagination?.pageSize || 10,
        search: searchTerm || undefined,
      };
      fetchSessions(params);
    },
    [fetchSessions, pagination?.pageSize, searchTerm]
  );

  /**
   * 페이지 크기 변경 처리
   */
  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      // URL 파라미터 업데이트는 템플릿의 useList에서 처리
      // 여기서는 직접 API 호출만 수행
      const params = {
        page: 1, // 페이지 크기 변경 시 첫 페이지로
        limit: newPageSize,
        search: searchTerm || undefined,
      };
      fetchSessions(params);
    },
    [fetchSessions, searchTerm]
  );

  return {
    // 상태
    sessions,
    loading,
    error,
    snackbarOpen,
    snackbarMessage,
    searchTerm,
    pagination: pagination || {
      page: 1,
      pageSize: 10,
      total: 0,
      size: 10,
    },

    // 핸들러
    onRefresh: loadChatLogData,
    onExport: handleExport,
    onSearchChange: handleSearchChange,
    onSnackbarClose: handleSnackbarClose,
    onViewSession: handleViewSession,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
  };
};
